import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { Box } from '@zillow/constellation-10';

import { DEEP_LINK_STEP, PROGRESS_STEP, PROGRESS_STEP_CONFIG, STEP } from '../../components/ListingClaim/constants';
import { ROUTES } from '../../constants/urls';

import { useAuth } from '../../components/AuthProvider';
import ListingClaimingSkeleton from '../../components/ListingClaim/ListingClaimingSkeleton';
import WizConvertListingInfo from '../../components/ListingClaim/WizConvertListingInfo';
import WizFindListingResults from '../../components/ListingClaim/WizFindListingResults';
import WizFindListingSuccess from '../../components/ListingClaim/WizFindListingSuccess';
import WizVerifyContactList from '../../components/ListingClaim/WizVerifyContactList';
import WizVerifyContactSuccess from '../../components/ListingClaim/WizVerifyContactSuccess';
import WizardStepController from '../../components/WizardStepController';
import AuthCheckWrapper from '../../containers/AuthCheckWrapper';

import type { WizardStepType } from '../../components/WizardStepController';

const STEP_CONFIG = [
  {
    stepKey: STEP.FIND_LISTING_RESULTS,
    wizardBodyTemplate: WizFindListingResults,
    progressStepKey: PROGRESS_STEP.FIND_LISTING,
  },
  {
    stepKey: STEP.FIND_LISTING_SUCCESS,
    wizardBodyTemplate: WizFindListingSuccess,
    progressStepKey: PROGRESS_STEP.VERIFY_CONTACT,
  },
  {
    stepKey: STEP.VERIFY_CONTACT_LIST,
    wizardBodyTemplate: WizVerifyContactList,
    progressStepKey: PROGRESS_STEP.VERIFY_CONTACT,
  },
  {
    stepKey: STEP.VERIFY_CONTACT_SUCCESS,
    wizardBodyTemplate: WizVerifyContactSuccess,
    progressStepKey: PROGRESS_STEP.VERIFY_CONTACT,
  },
  {
    stepKey: STEP.CONVERT_LISTING_INFO,
    wizardBodyTemplate: WizConvertListingInfo,
    progressStepKey: PROGRESS_STEP.CONVERT_LISTING,
  },
] as Array<WizardStepType>;

const ListingClaimingPage = () => {
  const router = useRouter();

  const { user } = useAuth();

  const paramStep = typeof router.query.step === 'string' ? router.query.step : undefined;
  const initialStepFromUrl = paramStep ? DEEP_LINK_STEP[paramStep] : undefined;
  const [currentStepKey, setCurrentStepKey] = useState(initialStepFromUrl ?? STEP.FIND_LISTING_RESULTS);

  useEffect(() => {
    if (router.isReady && user?.loggedIn && !user?.features?.canAccessFeedClaimAndConvert) {
      router.replace(ROUTES.PROPERTIES);
    }
  }, [router.isReady, user, router]);

  useEffect(() => {
    if (!router.isReady) return;
    const step = typeof router.query.step === 'string' ? router.query.step : undefined;
    const resolved = step ? DEEP_LINK_STEP[step] : undefined;
    if (resolved) {
      setCurrentStepKey(resolved);
    }
  }, [router.isReady, router.query.step]);

  const handleGoToStep = useCallback(
    (key: string) => {
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0);
      }
      setCurrentStepKey(key);
      router.replace({ pathname: router.pathname, query: { ...router.query, step: key } }, undefined, {
        shallow: true,
      });
    },
    [router],
  );

  return (
    <AuthCheckWrapper loading={<ListingClaimingSkeleton />} redirecting={<ListingClaimingSkeleton />}>
      <Box css={{ backgroundColor: 'bg.softer' }}>
        <WizardStepController
          steps={STEP_CONFIG}
          progressSteps={PROGRESS_STEP_CONFIG}
          currentStepKey={currentStepKey}
          onGoToStep={handleGoToStep}
        />
      </Box>
    </AuthCheckWrapper>
  );
};

export default ListingClaimingPage;
